/**
 * SyncView Component
 *
 * Full-page view for adjusting audio sync offset.
 * Curators navigate here from CurationTool, adjust the offset with nudge buttons
 * while playing, then save and return.
 *
 * URL: /admin/curate/sync?layer=melody
 * State from sessionStorage: syncViewState
 * Result stored in sessionStorage: syncViewResult
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlphaTabApi } from '@coderline/alphatab';
import { AlphaTabViewer } from '../../components/AlphaTabViewer';
import { useSyncedPlayback } from '../../hooks/useSyncedPlayback';
import { VideoPlayer, VideoPlayerRef } from '../../components/VideoPlayer';
import { ZoomRegionEditor } from '../../components/ZoomRegionEditor';
import { useSyncedVideoPlayback } from '../../hooks/useSyncedVideoPlayback';
import { LayerName, LAYER_LABELS, ZoomRegion } from '../../types/capsule';
import { mergeMusicXmlParts } from '../../utils/musicXmlMerger';
import { extractBpmFromFilename } from '../../utils/bpmExtractor';
import { retrieveFileForSync } from '../../lib/syncFileStore';
import '../../styles/SyncView.css';

/**
 * State passed to SyncView via sessionStorage
 */
interface SyncViewState {
  layer: LayerName;
  scoreFile: { name: string; data: string }; // base64
  audioFile?: { name: string; data: string }; // base64 (optional for video mode)
  videoFile?: { name: string; data: string }; // base64 (optional for audio mode)
  initialOffset: number;
  initialZoomRegions?: { rightHand?: ZoomRegion; leftHand?: ZoomRegion }; // For video mode
  capsuleSlug?: string; // For navigating back to the correct capsule
}

/**
 * Result returned from SyncView via sessionStorage
 */
interface SyncViewResult {
  layer: LayerName;
  offset: number;
  isVideoMode?: boolean;
  zoomRegions?: { rightHand?: ZoomRegion; leftHand?: ZoomRegion };
}

/**
 * Convert base64 string to Blob
 */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/**
 * Get MIME type from file extension
 */
function getMimeType(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop();
  const mimeTypes: Record<string, string> = {
    // Score files
    gp: 'application/octet-stream',
    gp3: 'application/octet-stream',
    gp4: 'application/octet-stream',
    gp5: 'application/octet-stream',
    gpx: 'application/octet-stream',
    xml: 'application/xml',
    musicxml: 'application/xml',
    // Audio files
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    m4a: 'audio/mp4',
    ogg: 'audio/ogg',
    // Video files
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
  };
  return mimeTypes[ext || ''] || 'application/octet-stream';
}

/**
 * Format milliseconds as a signed string
 */
function formatOffset(ms: number): string {
  const sign = ms >= 0 ? '+' : '';
  return `${sign}${ms}ms`;
}

/**
 * Format seconds as mm:ss
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function SyncView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [syncState, setSyncState] = useState<SyncViewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scoreUrl, setScoreUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // AlphaTab API state (using state instead of ref to trigger re-render when API becomes available)
  const [alphaTabApi, setAlphaTabApi] = useState<AlphaTabApi | null>(null);

  // Tempo state for cursor/audio sync
  const [scoreTempo, setScoreTempo] = useState<number | null>(null);
  const [audioTempo, setAudioTempo] = useState<number | null>(null);
  const [videoBpm, setVideoBpm] = useState<number | null>(null); // BPM extracted from video filename
  const [videoTempo, setVideoTempo] = useState<number>(100); // Video playback percentage (10-100%)

  // Video-related state
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const scoreContainerRef = useRef<HTMLElement | null>(null);
  const [zoomRegions, setZoomRegions] = useState<{
    rightHand?: ZoomRegion;
    leftHand?: ZoomRegion;
  }>({});
  const [editingZoomRegion, setEditingZoomRegion] = useState<'rightHand' | 'leftHand' | null>(null);

  // Get layer from URL params
  const layerParam = searchParams.get('layer') as LayerName | null;

  // Load state from sessionStorage on mount
  useEffect(() => {
    const urlsToRevoke: string[] = [];
    // Register cleanup so URLs created before an error are still revoked
    const cleanup = () => urlsToRevoke.forEach(url => URL.revokeObjectURL(url));

    try {
      const stateJson = sessionStorage.getItem('syncViewState');
      if (!stateJson) {
        navigate('/admin/curate', { replace: true });
        return;
      }

      const state: SyncViewState = JSON.parse(stateJson);

      // Validate layer matches URL param
      if (layerParam && state.layer !== layerParam) {
        const redirectUrl = state.capsuleSlug
          ? `/admin/curate?edit=${encodeURIComponent(state.capsuleSlug)}`
          : '/admin/curate';
        navigate(redirectUrl, { replace: true });
        return;
      }

      setSyncState(state);

      // Convert base64 to object URLs
      const scoreMimeType = getMimeType(state.scoreFile.name);
      let scoreBlob = base64ToBlob(state.scoreFile.data, scoreMimeType);

      // Apply MusicXML merger if this is an XML file
      const isMusicXml = state.scoreFile.name.toLowerCase().endsWith('.xml') ||
                         state.scoreFile.name.toLowerCase().endsWith('.musicxml');
      if (isMusicXml) {
        try {
          // Properly decode base64 to UTF-8 text (atob returns binary string, not UTF-8)
          const binaryString = atob(state.scoreFile.data);
          const bytes = Uint8Array.from(binaryString, c => c.charCodeAt(0));
          const xmlText = new TextDecoder('utf-8').decode(bytes);
          const mergeResult = mergeMusicXmlParts(xmlText);
          // Use processed XML if it was merged OR if instrument IDs were fixed
          const wasProcessed = mergeResult.wasMerged || mergeResult.xmlContent !== xmlText;
          if (wasProcessed) {
            const encoder = new TextEncoder();
            const xmlBytes = encoder.encode(mergeResult.xmlContent);
            scoreBlob = new Blob([xmlBytes], { type: 'application/xml' });
          }
        } catch {
          // Continue with original file
        }
      }

      const scoreObjUrl = URL.createObjectURL(scoreBlob);
      setScoreUrl(scoreObjUrl);
      urlsToRevoke.push(scoreObjUrl);

      // Handle audio file (if present)
      if (state.audioFile) {
        const audioMimeType = getMimeType(state.audioFile.name);
        const audioBlob = base64ToBlob(state.audioFile.data, audioMimeType);
        const audioObjUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioObjUrl);
        urlsToRevoke.push(audioObjUrl);

        // Extract BPM from audio filename if present
        const extractedBpm = extractBpmFromFilename(state.audioFile.name);
        if (extractedBpm) {
          setAudioTempo(extractedBpm);
        }
      }

      // Handle video file (if present)
      if (state.videoFile) {
        let videoBlob: Blob;

        // Check if video was passed via in-memory file store (for large files)
        if (state.videoFile.data === '__FILE_STORE__') {
          const storedFile = retrieveFileForSync('videoFile');
          if (!storedFile) {
            // Video file lost (page refresh) - redirect back to CurationTool
            const redirectUrl = state.capsuleSlug
              ? `/admin/curate?edit=${encodeURIComponent(state.capsuleSlug)}`
              : '/admin/curate';
            navigate(redirectUrl, { replace: true });
            return;
          }
          videoBlob = storedFile.file;
        } else {
          // Fallback to base64 decoding (for smaller files or backward compatibility)
          const videoMimeType = getMimeType(state.videoFile.name);
          videoBlob = base64ToBlob(state.videoFile.data, videoMimeType);
        }

        const videoObjUrl = URL.createObjectURL(videoBlob);
        setVideoUrl(videoObjUrl);
        urlsToRevoke.push(videoObjUrl);

        // Extract BPM from video filename if present
        const extractedVideoBpm = extractBpmFromFilename(state.videoFile.name);
        if (extractedVideoBpm) {
          setVideoBpm(extractedVideoBpm);
        }

        // Initialize zoom regions from state
        if (state.initialZoomRegions) {
          setZoomRegions(state.initialZoomRegions);
        }
      }
    } catch (err) {
      console.error('Failed to load sync state:', err);
      setError('Failed to load sync state. Please try again.');
    }

    // Cleanup object URLs on unmount
    return cleanup;
  }, [layerParam]);

  // Determine if we're in video mode
  const hasVideo = Boolean(syncState?.videoFile);
  const hasAudio = Boolean(syncState?.audioFile);

  // Initialize synced playback hook (for audio mode)
  const {
    isReady: audioIsReady,
    isPlaying: audioIsPlaying,
    offset: audioOffset,
    nudgeOffset: nudgeAudioOffset,
    resetOffset: resetAudioOffset,
    play: playAudio,
    pause: pauseAudio,
    stop: stopAudio,
    audioDuration,
    loading: audioLoading,
    error: playbackError,
  } = useSyncedPlayback({
    alphaTabApi: hasVideo ? null : alphaTabApi, // Disable audio playback in video mode
    audioUrl: hasVideo ? null : audioUrl,
    initialOffset: syncState?.initialOffset ?? 0,
  });

  // Calculate current tempo in BPM for video mode (percentage of video's native BPM)
  const currentVideoTempoBpm = videoBpm ? Math.round((videoBpm * videoTempo) / 100) : 120;

  // Initialize synced video playback hook (for video mode)
  const {
    isReady: videoIsReady,
    isPlaying: videoIsPlaying,
    offset: videoOffset,
    nudgeOffset: nudgeVideoOffset,
    resetOffset: resetVideoOffset,
    play: playVideo,
    pause: pauseVideo,
    stop: stopVideo,
  } = useSyncedVideoPlayback({
    alphaTabApi: hasVideo ? alphaTabApi : null,
    videoElement: hasVideo ? videoElement : null,
    initialOffset: syncState?.initialOffset ?? 0,
    scoreTempo: scoreTempo || 120,
    currentTempo: currentVideoTempoBpm,
  });

  // Unified playback state based on mode
  const isReady = hasVideo ? videoIsReady : audioIsReady;
  const isPlaying = hasVideo ? videoIsPlaying : audioIsPlaying;
  const offset = hasVideo ? videoOffset : audioOffset;
  const nudgeOffset = hasVideo ? nudgeVideoOffset : nudgeAudioOffset;
  const resetOffset = hasVideo ? resetVideoOffset : resetAudioOffset;
  const play = hasVideo ? playVideo : playAudio;
  const pause = hasVideo ? pauseVideo : pauseAudio;
  const baseStop = hasVideo ? stopVideo : stopAudio;

  // Wrapper for stop that also scrolls notation back to start
  const stop = useCallback(() => {
    baseStop();
    // Scroll the score container back to the start
    if (scoreContainerRef.current) {
      scoreContainerRef.current.scrollLeft = 0;
    }
  }, [baseStop]);

  // Handle AlphaTab ready - use setState to trigger re-render so useSyncedPlayback gets the API
  const handleAlphaTabReady = useCallback((api: AlphaTabApi) => {
    setAlphaTabApi(api);

    // Extract score tempo when score is loaded
    if (api.score && api.score.tempo > 0) {
      const tempo = api.score.tempo;
      setScoreTempo(tempo);
      // Only set audio tempo to score tempo if not already extracted from filename
      setAudioTempo(prev => prev ?? tempo);
    }
  }, []);

  // Apply playback speed when audio tempo changes (audio mode only)
  // Video mode playback speed is now handled by useSyncedVideoPlayback hook
  useEffect(() => {
    if (!hasVideo && alphaTabApi && scoreTempo && audioTempo && scoreTempo > 0) {
      const playbackSpeed = audioTempo / scoreTempo;
      alphaTabApi.playbackSpeed = playbackSpeed;
    }
  }, [alphaTabApi, scoreTempo, audioTempo, hasVideo]);

  // Handle audio tempo input change
  const handleAudioTempoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value > 0) {
      setAudioTempo(value);
    }
  }, []);

  // Keyboard shortcuts: P = play/pause, O = stop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const key = e.key.toLowerCase();
      if (key === 'p' && isReady) {
        e.preventDefault();
        if (isPlaying) {
          pause();
        } else {
          play();
        }
      } else if (key === 'o' && isReady) {
        e.preventDefault();
        stop();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReady, isPlaying, play, pause, stop]);

  // Build return URL based on capsule slug
  const getReturnUrl = useCallback(() => {
    if (syncState?.capsuleSlug) {
      return `/admin/curate?edit=${encodeURIComponent(syncState.capsuleSlug)}`;
    }
    return '/admin/curate';
  }, [syncState?.capsuleSlug]);

  // Handle back/cancel
  const handleCancel = useCallback(() => {
    navigate(getReturnUrl());
  }, [navigate, getReturnUrl]);

  // Handle save
  const handleSave = useCallback(() => {
    if (!syncState) return;

    const result: SyncViewResult = {
      layer: syncState.layer,
      offset,
      isVideoMode: hasVideo,
    };

    // Include zoom regions if in video mode and any are defined
    if (hasVideo && (zoomRegions.rightHand || zoomRegions.leftHand)) {
      result.zoomRegions = zoomRegions;
    }

    sessionStorage.setItem('syncViewResult', JSON.stringify(result));
    navigate(getReturnUrl());
  }, [syncState, offset, hasVideo, zoomRegions, navigate, getReturnUrl]);

  // Error state
  if (error) {
    return (
      <div className="sync-view">
        <div className="sync-view-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={handleCancel}>
            Back to Curation Tool
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (!syncState || !scoreUrl) {
    return (
      <div className="sync-view">
        <div className="sync-view-loading">
          <p>Loading sync view...</p>
        </div>
      </div>
    );
  }

  const layerLabel = LAYER_LABELS[syncState.layer] || syncState.layer;

  return (
    <div className={`sync-view ${hasVideo ? 'sync-view-video-mode' : ''}`}>
      {/* Header */}
      <header className="sync-header">
        <button className="sync-back-btn" onClick={handleCancel}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Curation
        </button>
        <h1 className="sync-title">Sync: {layerLabel} Layer</h1>
      </header>

      {/* Video Mode: Video on top, controls, horizontal AlphaTab, offset, zoom regions */}
      {hasVideo && videoUrl && (
        <>
          {/* Video Player with filename overlay and zoom region editor */}
          <section className="sync-video-section">
            {/* Filename overlay */}
            <div className="video-filename-overlay">{syncState.videoFile?.name}</div>
            <VideoPlayer
              ref={videoPlayerRef}
              src={videoUrl}
              zoomRegions={zoomRegions}
              onVideoReady={setVideoElement}
              style={{ height: '100%' }}
            />
            {/* ZoomRegionEditor overlay - only the drawing overlay, controls are external */}
            <ZoomRegionEditor
              videoElement={videoElement}
              rightHandRegion={zoomRegions.rightHand}
              leftHandRegion={zoomRegions.leftHand}
              onRegionChange={(hand, region) => {
                setZoomRegions(prev => ({ ...prev, [hand]: region }));
              }}
              onTestZoom={(region) => {
                if (videoPlayerRef.current) {
                  const video = videoPlayerRef.current.getVideoElement();
                  if (video) {
                    const scaleX = 100 / region.width;
                    const scaleY = 100 / region.height;
                    const scale = Math.min(scaleX, scaleY);
                    const centerX = region.x + region.width / 2;
                    const centerY = region.y + region.height / 2;
                    const translateX = (50 - centerX) * scale;
                    const translateY = (50 - centerY) * scale;
                    video.style.transformOrigin = '50% 50%';
                    video.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
                  }
                }
              }}
              onResetZoom={() => {
                videoPlayerRef.current?.setZoom('full');
              }}
              editingMode={editingZoomRegion}
              onEditingModeChange={setEditingZoomRegion}
              hideControls={true}
            />
          </section>

          {/* Compact playback controls row */}
          <div className="video-controls-row">
            <button
              className={`playback-btn ${isPlaying ? 'playing' : ''}`}
              onClick={isPlaying ? pause : play}
              disabled={!isReady}
            >
              {isPlaying ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21" />
                </svg>
              )}
            </button>
            <button className="stop-btn" onClick={stop} disabled={!isReady}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <rect x="4" y="4" width="16" height="16" />
              </svg>
            </button>

            <div className="controls-separator" />

            {/* BPM info */}
            {videoBpm && (
              <>
                <span className="control-label">BPM:</span>
                <span className="control-value bpm-display">{videoBpm}</span>
                {scoreTempo && scoreTempo !== videoBpm && (
                  <span className="control-hint">(score: {scoreTempo})</span>
                )}
                <div className="controls-separator" />
              </>
            )}

            {/* Speed slider */}
            <span className="control-label">Speed:</span>
            <button className="nudge-btn-small" onClick={() => setVideoTempo(Math.max(10, videoTempo - 10))}>-</button>
            <span className="control-value">{videoTempo}%</span>
            <button className="nudge-btn-small" onClick={() => setVideoTempo(Math.min(100, videoTempo + 10))}>+</button>

            <div className="controls-separator" />

            {/* Offset */}
            <span className="control-label">Offset:</span>
            <button className="nudge-btn-small" onClick={() => nudgeOffset(-100)} disabled={!isReady}>-100</button>
            <button className="nudge-btn-small" onClick={() => nudgeOffset(-10)} disabled={!isReady}>-10</button>
            <span className="control-value offset-display">{formatOffset(offset)}</span>
            <button className="nudge-btn-small" onClick={() => nudgeOffset(10)} disabled={!isReady}>+10</button>
            <button className="nudge-btn-small" onClick={() => nudgeOffset(100)} disabled={!isReady}>+100</button>
            <button className="reset-btn-small" onClick={resetOffset} disabled={!isReady}>Reset</button>

            <div className="controls-separator" />

            {/* Zoom buttons and region setup */}
            {editingZoomRegion ? (
              /* Editing mode UI */
              <>
                <span className="control-label editing-label">
                  Setting {editingZoomRegion === 'rightHand' ? 'Right Hand' : 'Left Hand'} region:
                </span>
                <span className="editing-hint">Draw rectangle on video</span>
                <button
                  className="zoom-btn clear-btn"
                  onClick={() => {
                    setZoomRegions(prev => ({ ...prev, [editingZoomRegion]: undefined }));
                  }}
                >
                  Clear
                </button>
                <button
                  className="zoom-btn done-btn"
                  onClick={() => {
                    setEditingZoomRegion(null);
                    videoPlayerRef.current?.setZoom('full');
                  }}
                >
                  Done
                </button>
              </>
            ) : (
              /* Normal zoom controls */
              <>
                <span className="control-label">Zoom:</span>
                <button
                  className={`zoom-btn ${!zoomRegions.rightHand && !zoomRegions.leftHand ? 'active' : ''}`}
                  onClick={() => videoPlayerRef.current?.setZoom('full')}
                >
                  Full
                </button>
                <button
                  className="zoom-btn"
                  disabled={!zoomRegions.rightHand}
                  onClick={() => {
                    const region = zoomRegions.rightHand;
                    if (region && videoPlayerRef.current) {
                      const video = videoPlayerRef.current.getVideoElement();
                      if (video) {
                        const scaleX = 100 / region.width;
                        const scaleY = 100 / region.height;
                        const scale = Math.min(scaleX, scaleY);
                        const centerX = region.x + region.width / 2;
                        const centerY = region.y + region.height / 2;
                        const translateX = (50 - centerX) * scale;
                        const translateY = (50 - centerY) * scale;
                        video.style.transformOrigin = '50% 50%';
                        video.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
                      }
                    }
                  }}
                >
                  Right Hand {zoomRegions.rightHand ? '✓' : ''}
                </button>
                <button
                  className="zoom-btn"
                  disabled={!zoomRegions.leftHand}
                  onClick={() => {
                    const region = zoomRegions.leftHand;
                    if (region && videoPlayerRef.current) {
                      const video = videoPlayerRef.current.getVideoElement();
                      if (video) {
                        const scaleX = 100 / region.width;
                        const scaleY = 100 / region.height;
                        const scale = Math.min(scaleX, scaleY);
                        const centerX = region.x + region.width / 2;
                        const centerY = region.y + region.height / 2;
                        const translateX = (50 - centerX) * scale;
                        const translateY = (50 - centerY) * scale;
                        video.style.transformOrigin = '50% 50%';
                        video.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
                      }
                    }
                  }}
                >
                  Left Hand {zoomRegions.leftHand ? '✓' : ''}
                </button>
                <div className="controls-separator" />
                <span className="control-label">Set Regions:</span>
                <button
                  className="zoom-btn set-btn"
                  onClick={() => {
                    setEditingZoomRegion('rightHand');
                    pause();
                  }}
                >
                  Set Right
                </button>
                <button
                  className="zoom-btn set-btn"
                  onClick={() => {
                    setEditingZoomRegion('leftHand');
                    pause();
                  }}
                >
                  Set Left
                </button>
              </>
            )}
          </div>

          {/* AlphaTab in horizontal mode */}
          <section
            className="sync-score-section sync-score-horizontal"
            ref={(el) => {
              // Get the AlphaTab notation container for scrolling
              if (el) {
                const alphaTabContainer = el.querySelector('[data-testid="notation-container"]');
                scoreContainerRef.current = alphaTabContainer as HTMLElement;
              }
            }}
          >
            <AlphaTabViewer
              scoreUrl={scoreUrl}
              onReady={handleAlphaTabReady}
              hidePlaybackControls={true}
              muteAudio={true}
              layoutMode="horizontal"
            />
          </section>
        </>
      )}

      {/* Audio Mode: AlphaTab Viewer - full page layout */}
      {!hasVideo && (
        <section className="sync-score-section">
          <AlphaTabViewer
            scoreUrl={scoreUrl}
            onReady={handleAlphaTabReady}
            hidePlaybackControls={true}
            muteAudio={true}
          />
        </section>
      )}

      {/* Audio Controls (when audio mode) */}
      {hasAudio && !hasVideo && (
        <>
          <section className="sync-audio-section">
            <div className="audio-info">
              <span className="audio-label">Audio:</span>
              <span className="audio-filename">{syncState.audioFile?.name}</span>
              <span className="audio-duration">{formatDuration(audioDuration)}</span>
              {audioTempo && (
                <span className="playback-bpm">
                  <strong>{audioTempo} BPM</strong>
                  {scoreTempo && scoreTempo !== audioTempo && (
                    <span className="bpm-adjusted"> (score: {scoreTempo})</span>
                  )}
                </span>
              )}
            </div>

            <div className="playback-controls-row">
              <button
                className={`playback-btn ${isPlaying ? 'playing' : ''}`}
                onClick={isPlaying ? pause : play}
                disabled={!isReady || audioLoading}
              >
                {isPlaying ? (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                    Pause
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21" />
                    </svg>
                    Play
                  </>
                )}
              </button>

              <button
                className="stop-btn"
                onClick={stop}
                disabled={!isReady || audioLoading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" />
                </svg>
                Stop
              </button>

              {audioLoading && <span className="loading-indicator">Loading audio...</span>}
              {playbackError && <span className="error-indicator">{playbackError}</span>}
            </div>
          </section>

          {/* Tempo Adjustment */}
          <section className="sync-tempo-section">
            <div className="tempo-header">
              <span className="tempo-label">Tempo Adjustment</span>
            </div>
            <div className="tempo-controls">
              <div className="tempo-field">
                <label htmlFor="score-tempo">Score BPM:</label>
                <input
                  id="score-tempo"
                  type="number"
                  value={scoreTempo ?? ''}
                  disabled
                  className="tempo-input"
                />
              </div>
              <div className="tempo-field">
                <label htmlFor="audio-tempo">Audio BPM:</label>
                <input
                  id="audio-tempo"
                  type="number"
                  value={audioTempo ?? ''}
                  onChange={handleAudioTempoChange}
                  min="20"
                  max="300"
                  step="1"
                  className="tempo-input"
                />
              </div>
              {scoreTempo && audioTempo && scoreTempo !== audioTempo && (
                <span className="tempo-ratio">
                  Speed: {(audioTempo / scoreTempo).toFixed(2)}x
                </span>
              )}
            </div>
            <p className="tempo-tip">
              Set the audio's actual BPM to sync cursor movement with the recording
            </p>
          </section>

          {/* Offset Adjustment */}
          <section className="sync-offset-section">
            <div className="offset-header">
              <span className="offset-label">Offset Adjustment</span>
              <span className="offset-value">{formatOffset(offset)}</span>
            </div>

            <div className="nudge-buttons">
              <button
                className="nudge-btn nudge-large"
                onClick={() => nudgeOffset(-100)}
                disabled={!isReady}
              >
                -100ms
              </button>
              <button
                className="nudge-btn nudge-small"
                onClick={() => nudgeOffset(-10)}
                disabled={!isReady}
              >
                -10ms
              </button>
              <button
                className="nudge-btn reset-btn"
                onClick={resetOffset}
                disabled={!isReady}
              >
                Reset
              </button>
              <button
                className="nudge-btn nudge-small"
                onClick={() => nudgeOffset(10)}
                disabled={!isReady}
              >
                +10ms
              </button>
              <button
                className="nudge-btn nudge-large"
                onClick={() => nudgeOffset(100)}
                disabled={!isReady}
              >
                +100ms
              </button>
            </div>

            <p className="offset-tip">
              Tip: Nudge until the cursor matches what you hear
            </p>
          </section>
        </>
      )}

      {/* Action Buttons */}
      <footer className="sync-actions">
        <button className="btn btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSave}>
          Save &amp; Done
        </button>
      </footer>
    </div>
  );
}

export default SyncView;
